/**
 * Privacy & Safety Overview page — public, no auth, linkable.
 *
 * Renders the calm overview document directly in-app so partners,
 * safeguarding leads, parents, and reviewers can read it without
 * downloading the PDF (PDF is offered as a download alongside).
 *
 * The content here mirrors /app/memory/privacy_and_safety_overview.md
 * and /app/frontend/public/privacy_and_safety_overview.pdf. If Louise
 * edits the markdown source, this file can be updated in lockstep.
 */
import { Download, ArrowLeft, Mail } from "lucide-react";

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10" data-testid={`privacy-section-${id}`}>
    <h2 className="text-xl sm:text-2xl font-bold text-[#A55B64] mb-3">{title}</h2>
    <div className="text-[15px] leading-relaxed text-[#4A4A4A] space-y-3">{children}</div>
  </section>
);

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#4A4A4A]" data-testid="privacy-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E8E5DC]">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <a
            href="/"
            data-testid="privacy-back-home"
            className="inline-flex items-center gap-2 text-sm text-[#6B6B6B] hover:text-[#4A4A4A] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Poodle Talk
          </a>
          <a
            href="/privacy_and_safety_overview.pdf"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="privacy-pdf-download"
            className="inline-flex items-center gap-2 bg-[#A55B64] hover:bg-[#8A4951] text-white px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Download PDF
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10">
        {/* Cover */}
        <div className="mb-12">
          <p className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-2">Poodles &amp; Friends · United Kingdom</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#A55B64] mb-3">Privacy &amp; Safety Overview</h1>
          <p className="text-[15px] text-[#6B6B6B] leading-relaxed">
            An honest, plain-English summary of how Poodle Talk handles privacy, data, AI processing, and child safety — for parents, teachers, nurseries, schools, charities, and safeguarding leads.
          </p>
          <div className="mt-6 p-4 bg-[#F9F6EE] rounded-2xl border border-[#FFE5C2] text-[14px] leading-relaxed">
            <strong>At a glance:</strong> No chat content stored · No third-party trackers · No advertising · No biometrics · No data sold or shared · AI provider does not train on our messages · All child input is treated as plain text only.
          </div>
          <p className="mt-4 text-[13px] text-[#6B6B6B]">
            Companion document: <a href="/safeguarding_stress_test.pdf" target="_blank" rel="noopener noreferrer" className="underline text-[#A55B64]">AI Safeguarding Stress Test Report (PDF)</a> — 54 high-risk scenarios with verbatim AI responses.
          </p>
        </div>

        <Section id="who-we-are" title="1. Who we are">
          <p>
            Poodle Talk is made by <strong>Poodles &amp; Friends</strong>, a small UK family project that creates gentle media and tools for children, with a particular focus on SEND (Special Educational Needs and Disabilities) accessibility. We are not a clinical service, not a large platform, and not funded by advertisers.
          </p>
          <ul className="list-disc list-inside ml-1 space-y-1">
            <li>Website: <a href="https://poodlesandfriends.uk" target="_blank" rel="noopener noreferrer" className="underline text-[#A55B64]">poodlesandfriends.uk</a></li>
            <li>App: <a href="https://poodletalk.poodlesandfriends.uk" target="_blank" rel="noopener noreferrer" className="underline text-[#A55B64]">poodletalk.poodlesandfriends.uk</a></li>
            <li>Contact: <a href="mailto:hello@poodlesandfriends.uk" className="underline text-[#A55B64]">hello@poodlesandfriends.uk</a></li>
            <li>Country of operation: United Kingdom</li>
            <li>ICO: Registered with the Information Commissioner&apos;s Office. We process personal data in accordance with UK data protection legislation.</li>
          </ul>
        </Section>

        <Section id="data-we-collect" title="2. What data we do — and do not — collect">
          <p>
            We have intentionally designed Poodle Talk so that <strong>chat content is never stored on our servers</strong>. The poodle's reply is generated in real time and forgotten as soon as the conversation moves on.
          </p>
          <p className="font-semibold text-[#4A4A4A]">What we do not collect from children</p>
          <ul className="list-disc list-inside ml-1 space-y-1">
            <li>No name, age, school, location, or contact details</li>
            <li>No photographs, no microphone recordings, no uploaded files</li>
            <li>No behavioural tracking across other websites</li>
            <li>No advertising identifiers</li>
            <li>No chat content saved on our servers</li>
            <li>No third-party analytics (no Google Analytics, no Facebook Pixel, no Mixpanel, no Hotjar)</li>
          </ul>
          <p className="font-semibold text-[#4A4A4A] mt-4">What we do store, and only where strictly necessary</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-[#E8E5DC] rounded-lg overflow-hidden">
              <thead className="bg-[#FCEAEA] text-[#A55B64]">
                <tr>
                  <th className="text-left p-2.5 font-semibold">Data</th>
                  <th className="text-left p-2.5 font-semibold">Where</th>
                  <th className="text-left p-2.5 font-semibold">Why</th>
                </tr>
              </thead>
              <tbody className="text-[13px] [&_td]:p-2.5 [&_td]:border-t [&_td]:border-[#E8E5DC] [&_td]:align-top">
                <tr><td>Anonymous device ID (UUID)</td><td>On the child's device (localStorage)</td><td>So the free-trial credit count travels between sessions on the same device</td></tr>
                <tr><td>Total messages used (a number)</td><td>Our database</td><td>To run the credit system fairly</td></tr>
                <tr><td>Parent email at purchase</td><td>Stripe + our database</td><td>To send the credit code by email</td></tr>
                <tr><td>Google name + email (only if a parent signs in)</td><td>Our database</td><td>So credits follow the family between WiFi and mobile data</td></tr>
                <tr><td>Mood check-ins (if used)</td><td>The child's device only</td><td>A private mood diary — never transmitted</td></tr>
              </tbody>
            </table>
          </div>
          <p>We hold no clinical, biometric, or special-category data of any kind.</p>
        </Section>

        <Section id="how-ai-works" title="3. How the AI works">
          <p>The poodle characters are powered by <strong>Claude Sonnet 4.5</strong>, an AI model created by Anthropic.</p>
          <ul className="list-disc list-inside ml-1 space-y-1">
            <li>Messages are sent over an encrypted (HTTPS) connection, receive a reply, and are then released.</li>
            <li>We use Anthropic's commercial API. Per their <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="underline text-[#A55B64]">published API privacy policy</a>, messages sent through the API are <strong>not used to train AI models</strong>.</li>
            <li>The poodle is shaped by a strict, child-safe system prompt — refuses medical / legal / religious / therapeutic advice, redirects to trusted adults.</li>
            <li>The AI is never positioned as a child's only source of support.</li>
          </ul>
          <p>A full <a href="/safeguarding_stress_test.pdf" target="_blank" rel="noopener noreferrer" className="underline text-[#A55B64]">AI Safeguarding Stress Test Report (PDF)</a> documenting 54 high-risk scenarios is published separately.</p>
        </Section>

        <Section id="child-input-safety" title="4. How we handle child input safely">
          <p>Everything a child types or taps is treated as <strong>plain text only</strong>. We have audited this at multiple layers:</p>
          <ul className="list-disc list-inside ml-1 space-y-1">
            <li>React auto-escapes any text the user enters — HTML tags and scripts render as literal text and cannot run.</li>
            <li>The application <strong>does not</strong> use <code className="bg-[#F3F0E6] px-1.5 py-0.5 rounded text-[12px]">innerHTML</code> or <code className="bg-[#F3F0E6] px-1.5 py-0.5 rounded text-[12px]">dangerouslySetInnerHTML</code> anywhere in the chat flow.</li>
            <li>The session cookie is <code className="bg-[#F3F0E6] px-1.5 py-0.5 rounded text-[12px]">HttpOnly</code>, <code className="bg-[#F3F0E6] px-1.5 py-0.5 rounded text-[12px]">Secure</code>, and <code className="bg-[#F3F0E6] px-1.5 py-0.5 rounded text-[12px]">SameSite=Lax</code>.</li>
            <li>No file uploads accepted from the chat interface.</li>
            <li>No external links submitted by children are rendered as clickable links.</li>
          </ul>
        </Section>

        <Section id="cookies" title="5. Cookies and analytics">
          <p>Poodle Talk uses no advertising cookies, no third-party analytics, and no cross-site trackers.</p>
          <p>The only cookie we set is the session cookie used to keep a parent signed in (if they choose to sign in via Google) so that purchased credits follow them between WiFi and mobile data. This cookie is essential to the service and therefore exempt from cookie-banner requirements under UK ePrivacy Regulations and the EU ePrivacy Directive.</p>
          <p>We do keep a small number of anonymous, aggregated engagement counters (which picture-card categories are tapped, which age mode is used). These are counts only — no chat content, no names, no personal data.</p>
        </Section>

        <Section id="email" title="6. Email communication">
          <p>When you purchase credits or sign in, you may receive an email from us (sent via our email partner, Resend). These emails include basic open-and-click tracking so we can see whether the email reached the inbox and whether the link was used.</p>
          <p>You can unsubscribe at any time using the link at the bottom of any email. We never sell or share email lists with any third party.</p>
        </Section>

        <Section id="childrens-privacy" title="7. Children's privacy — UK GDPR / EU GDPR / COPPA">
          <p>Poodle Talk is designed to be safely used by children, but it is <strong>the parent or supervising adult</strong> who is treated as the data subject for any data we hold.</p>
          <ul className="list-disc list-inside ml-1 space-y-1">
            <li><strong>UK GDPR / EU GDPR:</strong> lawful bases used are <em>legitimate interest</em> (anonymous device ID), <em>consent</em> (optional Google sign-in), and <em>contract</em> (Stripe purchase).</li>
            <li><strong>COPPA (US):</strong> we do not knowingly collect personal information from children under 13. Sign-in and purchasing are designed for parents and supervising adults only.</li>
            <li><strong>UK Children's Code (ICO Age-Appropriate Design Code):</strong> applied as a design lens — high privacy by default, no nudging, no profiling, no geolocation, no behavioural advertising.</li>
          </ul>
        </Section>

        <Section id="your-rights" title="8. Your rights and how to use them">
          <p>Under UK / EU data protection law you have the right to access, correct, delete, restrict, object to, or port the personal data we hold about you.</p>
          <div className="p-4 bg-[#F9F6EE] rounded-2xl border border-[#FFE5C2] flex items-start gap-3" data-testid="data-request-callout">
            <Mail className="w-5 h-5 text-[#A55B64] mt-0.5 flex-shrink-0" />
            <div>
              <p>
                Email <a href="mailto:hello@poodlesandfriends.uk?subject=DATA%20REQUEST" className="underline text-[#A55B64] font-semibold">hello@poodlesandfriends.uk</a> with subject line including the words <strong>DATA REQUEST</strong> and a brief description of what you'd like us to do.
              </p>
              <p className="mt-1 text-[13px] text-[#6B6B6B]">We respond within 30 days. There is no cost.</p>
            </div>
          </div>
        </Section>

        <Section id="safeguarding" title="9. Safeguarding stance">
          <p>Poodle Talk is a gentle communication tool — <strong>not a therapy product, not a clinical AAC tool, and not a replacement for adult support</strong>.</p>
          <ul className="list-disc list-inside ml-1 space-y-1">
            <li>It will never claim to diagnose, treat, or replace professional support.</li>
            <li>Defers to trusted adults, crisis lines, or emergency services for serious matters.</li>
            <li>UK first: Childline 0800 1111, SHOUT 85258, Samaritans 116 123, 999.</li>
            <li>International fallback: <a href="https://findahelpline.com" target="_blank" rel="noopener noreferrer" className="underline text-[#A55B64]">findahelpline.com</a>.</li>
            <li>Picture cards surface crisis numbers when tapped on safety-related prompts.</li>
          </ul>
        </Section>

        <Section id="updates" title="10. Updates to this document">
          <p>This Privacy &amp; Safety Overview is reviewed each time we ship a meaningful change to the app. To receive a fresh copy whenever it changes, email us at <a href="mailto:hello@poodlesandfriends.uk" className="underline text-[#A55B64]">hello@poodlesandfriends.uk</a>.</p>
          <p className="mt-6 text-[#6B6B6B]">— Louise and the Poodles &amp; Friends team 💛🐩</p>
          <p className="mt-3 text-xs text-[#9A9A9A]" data-testid="privacy-last-updated">Updated May 2026</p>
        </Section>
      </main>

      <footer className="border-t border-[#E8E5DC] py-6 text-center text-xs text-[#9A9A9A]">
        <p>Poodles &amp; Friends · United Kingdom · <a href="https://poodlesandfriends.uk" target="_blank" rel="noopener noreferrer" className="underline">poodlesandfriends.uk</a></p>
        <p className="mt-2 max-w-xl mx-auto px-4" data-testid="ico-footer-note">Registered with the Information Commissioner&apos;s Office (ICO). We process personal data in accordance with UK data protection legislation.</p>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
